'use client'

import { useState } from 'react'

import { IconChevLeft, IconChevRight, IconGallery } from '@/app/components/atoms/Icons'
import { PopUpSection } from '@/app/components/shared/PopUpSection'
import type { PopUpSectionItem } from '@/app/components/shared/PopUpSection'
import { cx } from '@/app/lib/utils'

import { CAROUSEL_SECTION_LABEL } from '../HeroGaleria.constants'
import type { HeroGaleriaMosaicTile } from '../HeroGaleria.types'
import { HeroGaleriaMosaicPage } from '../HeroGaleriaMosaicPage'
import styles from './HeroGaleriaCarousel.module.scss'
import type { HeroGaleriaCarouselProps } from './HeroGaleriaCarousel.types'

const MIN_TILES_FOR_GALLERY_BUTTON = 3

function toPopUpSectionItem(tile: HeroGaleriaMosaicTile): PopUpSectionItem {
  return tile.kind === 'video'
    ? { id: tile.id, kind: 'video', youtubeId: tile.videoId, title: tile.title }
    : { id: tile.id, kind: 'image', src: tile.src, alt: tile.alt }
}

/**
 * Vista interactiva del mosaico — navegación entre páginas (5-8 celdas,
 * FR-015/FR-016/FR-017) con únicamente flechas anterior/siguiente reales,
 * deshabilitadas en cada extremo (nunca ocultas; sin barra de posición ni
 * puntos, fiel a `MultimediaBentoCarousel.tsx` de la referencia,
 * research.md §16); y, con 3 o más celdas en total, un botón "Ver galería
 * (N)" que abre el mismo pop-up compartido (`PopUpSection`) que el módulo
 * Multimedia, con todos los elementos del mosaico (todas las páginas, no
 * solo la visible) — research.md §17.
 */
export function HeroGaleriaCarousel({ pages }: HeroGaleriaCarouselProps) {
  const [activePage, setActivePage] = useState(0)
  const [popUpOpen, setPopUpOpen] = useState(false)

  if (pages.length === 0) return null

  const currentPage = pages[activePage]
  if (!currentPage) return null

  const canPrev = activePage > 0
  const canNext = activePage < pages.length - 1

  const allTiles = pages.flatMap((page) => page.tiles)
  const showGalleryButton = allTiles.length >= MIN_TILES_FOR_GALLERY_BUTTON
  const initialIndex = pages.slice(0, activePage).reduce((count, page) => count + page.tiles.length, 0)

  return (
    <div className={styles.root} role="region" aria-label={CAROUSEL_SECTION_LABEL}>
      <div className={styles.pageArea}>
        <HeroGaleriaMosaicPage page={currentPage} indexOffset={initialIndex} />

        {pages.length > 1 && (
          <>
            <button
              type="button"
              className={cx(styles.nav, styles.navPrev)}
              onClick={() => setActivePage((page) => page - 1)}
              disabled={!canPrev}
              aria-label="Página anterior"
            >
              <IconChevLeft size={20} />
            </button>
            <button
              type="button"
              className={cx(styles.nav, styles.navNext)}
              onClick={() => setActivePage((page) => page + 1)}
              disabled={!canNext}
              aria-label="Página siguiente"
            >
              <IconChevRight size={20} />
            </button>
          </>
        )}

        {showGalleryButton && (
          <button type="button" className={styles.galleryBtn} onClick={() => setPopUpOpen(true)}>
            <IconGallery size={20} />
            Ver galería ({allTiles.length})
          </button>
        )}
      </div>

      {popUpOpen && (
        <PopUpSection
          items={allTiles.map(toPopUpSectionItem)}
          initialIndex={initialIndex}
          ariaLabel={CAROUSEL_SECTION_LABEL}
          onClose={() => setPopUpOpen(false)}
        />
      )}
    </div>
  )
}
